/**
 * Registro attività della società (V53) e della piattaforma (V57): forma dei
 * dati, filtri in query string e la frase di ogni riga.
 *
 * La frase si scrive solo dal dettaglio salvato con la riga, mai da cio che
 * esiste oggi: una squadra rinominata o un account cancellato non cambiano
 * la storia. Un'azione che questa versione non conosce mostra il suo codice.
 */

import {
  type FiltriRegistroPiattaforma,
  dataLocale,
  filtriRegistroPiattaformaSchema,
  oraLocale,
} from '@calcio/shared';
import { dataBreve } from '../comuni/dati.js';
import { ETICHETTA_RUOLO, type RuoloAccount, nomeSquadra } from './ruoli.js';

export interface VoceRegistro {
  id: string;
  creatoIl: string;
  azione: string;
  autore: { id: string; nome: string } | null;
  /** Null per le righe di una società cancellata. */
  societa: { id: string; nome: string } | null;
  entita: string | null;
  entitaId: string | null;
  dettaglio: Record<string, unknown>;
  entitaPresente: boolean;
}

export interface PaginaRegistro {
  voci: VoceRegistro[];
  autori: { id: string; nome: string }[];
  prossimo: string | null;
  /** Solo nel registro di piattaforma: tutte le società, per il filtro. */
  societa?: { id: string; nome: string }[];
}

/** I filtri della vista: il cursore non sta nell'indirizzo, si carica dal fondo. */
export type FiltriVista = Omit<FiltriRegistroPiattaforma, 'dopo'>;

const CHIAVI = ['autore', 'tipo', 'da', 'a', 'societa'] as const;

/**
 * Un indirizzo scritto a mano con un filtro non valido apre il registro senza
 * filtri. Il filtro per società vale solo nel registro di piattaforma.
 */
export function filtriDaSearch(search: string, piattaforma = false): FiltriVista {
  const q = new URLSearchParams(search);
  const letti = filtriRegistroPiattaformaSchema.safeParse(
    Object.fromEntries(CHIAVI.map((k) => [k, q.get(k) ?? ''])),
  );
  if (!letti.success) return {};
  const { societa, ...resto } = letti.data;
  return piattaforma && societa ? { ...resto, societa } : resto;
}

/** "?tipo=ruoli&da=2026-09-01", o la stringa vuota senza filtri. */
export function queryRegistro(filtri: FiltriRegistroPiattaforma): string {
  const q = new URLSearchParams();
  for (const k of [...CHIAVI, 'dopo'] as const) {
    const v = filtri[k];
    if (v) q.set(k, v);
  }
  const coda = q.toString();
  return coda ? `?${coda}` : '';
}

/** "17/09/2026 alle 18:30", nell'ora italiana. */
export const quando = (istante: string): string =>
  `${dataBreve(dataLocale(istante))} alle ${oraLocale(istante)}`;

const testo = (v: unknown): string | null => (typeof v === 'string' && v ? v : null);
const numero = (v: unknown): number | null => (typeof v === 'number' ? v : null);

const ETICHETTA_LIMITE: Record<string, string> = {
  maxSquadreStagione: 'squadre per stagione',
  maxGiocatori: 'anagrafiche',
  maxAccount: 'account',
  maxMessaggiAssistente: "domande all'assistente al mese",
};

/** "account da 200 a 250, anagrafiche da 3000 a 4000". */
function limitiCambiati(prima: unknown, dopo: unknown): string {
  if (!prima || !dopo || typeof prima !== 'object' || typeof dopo !== 'object') return '';
  const p = prima as Record<string, unknown>;
  const d = dopo as Record<string, unknown>;
  return Object.keys(ETICHETTA_LIMITE)
    .filter((k) => numero(p[k]) !== null && numero(p[k]) !== numero(d[k]))
    .map((k) => `${ETICHETTA_LIMITE[k]} da ${numero(p[k])} a ${numero(d[k])}`)
    .join(', ');
}

const quanti = (n: number | null, uno: string, molti: string): string | null =>
  n ? `${n} ${n === 1 ? uno : molti}` : null;

/** "Mister di Allievi 2026/27", o il solo ruolo se la squadra non c'e. */
function ruoloDi(d: Record<string, unknown>): string {
  const ruolo = testo(d.ruolo);
  const etichetta = (ruolo && ETICHETTA_RUOLO[ruolo as RuoloAccount]) ?? ruolo ?? 'un ruolo';
  const squadra = testo(d.squadra);
  if (!squadra) return etichetta;
  const stagione = testo(d.stagione);
  const nome = nomeSquadra({ squadra, categoria: testo(d.categoria) });
  return `${etichetta} di ${nome}${stagione ? ` ${stagione}` : ''}`;
}

export function fraseVoce(voce: Pick<VoceRegistro, 'azione' | 'dettaglio'>): string {
  const d = voce.dettaglio;
  const persona = testo(d.account) ?? 'un account';
  const email = testo(d.email);

  switch (voce.azione) {
    case 'ruolo.assegnato':
      return `Ruolo di ${ruoloDi(d)} assegnato a ${persona}`;
    case 'ruolo.revocato':
      return `Ruolo di ${ruoloDi(d)} revocato a ${persona}`;
    case 'account.disattivato':
      return `Account di ${persona} disattivato`;
    case 'account.riattivato':
      return `Account di ${persona} riattivato`;
    case 'account.invitato':
      return `Invito inviato a ${persona}${email ? ` all'indirizzo ${email}` : ''}`;
    case 'invito.reinviato':
      return `Invito di ${persona} inviato di nuovo${email ? ` a ${email}` : ''}`;
    case 'invito.annullato':
      return `Invito di ${persona} annullato e account cancellato`;
    case 'invito.accettato':
      return `Invito accettato da ${persona}`;
    case 'allenamento.eliminato': {
      const squadra = testo(d.squadra);
      const dove = squadra
        ? ` di ${nomeSquadra({ squadra, categoria: testo(d.categoria) })}${testo(d.stagione) ? ` ${testo(d.stagione)}` : ''}`
        : '';
      const istante = testo(d.dataOra);
      const data = istante ? ` del ${quando(istante)}` : '';
      const perse = [
        quanti(numero(d.presenze), 'presenza', 'presenze'),
        quanti(numero(d.voti), 'voto', 'voti'),
        quanti(numero(d.gruppi), 'gruppo', 'gruppi'),
        quanti(numero(d.blocchi), 'blocco', 'blocchi'),
        quanti(numero(d.log), 'log', 'log'),
      ].filter(Boolean);
      return `Allenamento${dove}${data} eliminato${perse.length ? `, con ${perse.join(' e ')}` : ''}`;
    }
    case 'blocco.eliminato': {
      const nome = testo(d.nome) ?? 'un blocco';
      const gruppo = testo(d.gruppo);
      const nLog = numero(d.log);
      const conLog = nLog ? `, con ${quanti(nLog, 'log', 'log')}` : '';
      return `Blocco «${nome}»${gruppo ? ` del gruppo ${gruppo}` : ''} eliminato${conLog}`;
    }
    case 'attributo.eliminato':
      return `Attributo «${testo(d.nome) ?? '?'}» eliminato dal catalogo`;
    case 'misurazione.eliminata': {
      const valore = numero(d.valore);
      // Le voci scritte prima della scala 1-100 portano ancora l'unità delle misure.
      const unita = testo(d.unita);
      const data = testo(d.data);
      const misura =
        valore === null ? '' : `: ${valore.toLocaleString('it-IT')}${unita ? ` ${unita}` : ''}`;
      return `Misurazione di ${testo(d.attributo) ?? 'un attributo'} di ${testo(d.giocatore) ?? 'un giocatore'}${data ? ` del ${dataBreve(data)}` : ''} eliminata${misura}`;
    }
    case 'test.eliminato':
      return `Test «${testo(d.nome) ?? '?'}» eliminato dalla libreria`;
    case 'esecuzione.eliminata': {
      const data = testo(d.data);
      const perse = [
        quanti(numero(d.giocatori), 'giocatore', 'giocatori'),
        quanti(numero(d.misurazioni), 'misurazione', 'misurazioni'),
      ].filter(Boolean);
      return `Esecuzione del test «${testo(d.test) ?? '?'}»${data ? ` del ${dataBreve(data)}` : ''} eliminata${perse.length ? `, con ${perse.join(' e ')}` : ''}`;
    }
    case 'competizione.eliminata':
      return `Competizione «${testo(d.nome) ?? '?'}» eliminata`;
    case 'admin.nominato': {
      const via = d.invito === true && email ? `, con l'invito a ${email}` : '';
      const prima = testo(d.precedente);
      return `${persona} nominato Admin Squadra${via}${prima ? ` al posto di ${prima}` : ''}`;
    }
    case 'societa.creata':
      return `Società «${testo(d.societa) ?? '?'}» creata`;
    case 'societa.disattivata':
      return `Società «${testo(d.societa) ?? '?'}» disattivata`;
    case 'societa.riattivata':
      return `Società «${testo(d.societa) ?? '?'}» riattivata`;
    case 'societa.limiti_modificati': {
      const cambi = limitiCambiati(d.prima, d.dopo);
      return `Limiti di «${testo(d.societa) ?? '?'}» modificati${cambi ? `: ${cambi}` : ''}`;
    }
    default:
      return voce.azione;
  }
}
